import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Edit2, X, GripVertical, Play, Layers, Clock, List, Image, Video, Globe, Zap, Check, AlertCircle, FileText } from 'lucide-react';
import PlaylistPreview from './PlaylistPreview';
import WidgetPicker from './WidgetPicker';

const API_URL = process.env.REACT_APP_API_URL;
const API_BASE_URL = (process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://app.alpenscreen.ch');

const PlaylistManager = () => {
  const { t } = useTranslation();
  const [playlists, setPlaylists] = useState([]);
  const [media, setMedia] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [newPlaylist, setNewPlaylist] = useState({ name: '', description: '', loop: true });
  const [selectedMediaIds, setSelectedMediaIds] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPlaylist, setPreviewPlaylist] = useState(null);
  const [showWidgetPicker, setShowWidgetPicker] = useState(false);
  
  // States for inline duration editing
  const [editingItemId, setEditingItemId] = useState(null);
  const [editDuration, setEditDuration] = useState('');
  
  // Drag & Drop States
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [playlistsRes, mediaRes] = await Promise.all([
        fetch(`${API_URL}/playlists`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/media`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const playlistsData = await playlistsRes.json();
      const mediaData = await mediaRes.json();
      
      setPlaylists(Array.isArray(playlistsData) ? playlistsData : []);
      setMedia(Array.isArray(mediaData) ? mediaData : []);
      
      console.log('🎵 PlaylistManager loaded:', {
        playlists: Array.isArray(playlistsData) ? playlistsData.length : 0,
        media: Array.isArray(mediaData) ? mediaData.length : 0
      });
    } catch (error) {
      console.error(t('errors.loading.playlists'), error);
      setPlaylists([]);
      setMedia([]);
    }
  };

  // Update duration for individual playlist item
  const updateItemDuration = async (playlistId, itemId, newDuration) => {
    try {
      const token = localStorage.getItem('token');
      const duration = parseInt(newDuration);
      
      if (isNaN(duration) || duration < 1 || duration > 3600) {
        alert(t('playlists.enterDuration'));
        return;
      }
      
      console.log('⏱️ Updating item duration:', { playlistId, itemId, duration });
      
      const response = await fetch(
        `${API_URL}/playlists/${playlistId}/items/${itemId}/duration`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ override_duration: duration })
        }
      );
      
      if (!response.ok) {
        throw new Error(t('playlists.durationUpdateFailed'));
      }
      
      console.log('✅ Duration updated successfully');
      
      // Reload complete data from backend
      await loadData();
      
      // Refresh the selected playlist with updated data
      const playlistsRes = await fetch(`${API_URL}/playlists`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const playlistsData = await playlistsRes.json();
      const updatedPlaylist = playlistsData.find(p => p.id === playlistId);
      
      if (updatedPlaylist) {
        setSelectedPlaylist(updatedPlaylist);
        // ✅ FIXED: Verwende media_id für Media Items
        const existingMediaIds = updatedPlaylist.media_items
          ?.filter(item => item.item_type === 'media')
          .map(m => m.media_id || m.id) || [];
        setSelectedMediaIds(existingMediaIds);
      }
      
      // Reset editing state
      setEditingItemId(null);
      setEditDuration('');
      
    } catch (error) {
      console.error('❌ Error updating duration:', error);
      alert(error.message || t('playlists.durationUpdateFailed'));
    }
  };

  // Start editing duration
  const startEditingDuration = (item) => {
    setEditingItemId(item.playlist_item_id || item.id);
    setEditDuration(item.duration?.toString() || '10');
  };

  // Cancel editing
  const cancelEditingDuration = () => {
    setEditingItemId(null);
    setEditDuration('');
  };

  // Save duration on Enter key
  const handleDurationKeyPress = (e, playlistId, itemId) => {
    if (e.key === 'Enter') {
      updateItemDuration(playlistId, itemId, editDuration);
    } else if (e.key === 'Escape') {
      cancelEditingDuration();
    }
  };

  // Drag & Drop Handlers
  const handleDragStart = (e, item, index) => {
    setDraggedItem({ item, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.index === dropIndex) {
      setDraggedItem(null);
      setDragOverIndex(null);
      return;
    }
    
    // Reorder items
    const items = [...selectedPlaylist.media_items];
    const [removed] = items.splice(draggedItem.index, 1);
    items.splice(dropIndex, 0, removed);
    
    // Update state
    setSelectedPlaylist({
      ...selectedPlaylist,
      media_items: items
    });
    
    setDraggedItem(null);
    setDragOverIndex(null);
    
    console.log('🔄 Items reordered');
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const createPlaylist = async () => {
    if (!newPlaylist.name) {
      alert(t('errors.validation.required'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/playlists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newPlaylist,
          media_ids: selectedMediaIds
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        if (error.error && error.error.includes('duplicate key')) {
          throw new Error(t('errors.playlists.duplicateName'));
        }
        throw new Error(error.error || t('errors.playlists.createFailed'));
      }
      
      await loadData();
      setShowCreateModal(false);
      setNewPlaylist({ name: '', description: '', loop: true });
      setSelectedMediaIds([]);
      
      alert(t('success.created', { name: t('playlists.title') }));
    } catch (error) {
      alert(t('errors.playlists.createError') + ': ' + error.message);
    }
  };

  // ✅ FIXED: Korrekte ID-Handhabung beim Speichern
  const updatePlaylistItems = async (playlistId) => {
    try {
      const token = localStorage.getItem('token');
      
      // ✅ FIXED: Map items mit korrekten IDs
      const allItems = (selectedPlaylist.media_items || []).map((item, index) => {
        const mappedItem = {
          item_type: item.item_type,
          position: index,
          duration: item.duration || 10
        };
        
        if (item.item_type === 'media') {
          // ✅ FIXED: Für MEDIA verwende immer media_id (Backend sendet es als media_id)
          mappedItem.media_id = item.media_id || item.id;
          
        } else if (item.item_type === 'widget') {
          // Für WIDGETS: Verwende feature_instance_id
          mappedItem.feature_instance_id = item.feature_instance_id || item.id;
        }
        
        return mappedItem;
      });
      
      console.log('💾 Saving playlist items:', {
        playlistId,
        itemCount: allItems.length,
        items: allItems
      });
      
      const response = await fetch(`${API_URL}/playlists/${playlistId}/items/complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ items: allItems })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('errors.playlists.updateFailed'));
      }
      
      const result = await response.json();
      
      // ✅ Show warning if items were filtered
      if (result.itemsFiltered && result.itemsFiltered > 0) {
        alert(`${t('success.updated', { name: t('playlists.title') })}\n\n⚠️ ${result.itemsFiltered} item(s) were removed because they no longer exist.`);
      } else {
        alert(t('success.updated', { name: t('playlists.title') }));
      }
      
      await loadData();
      setShowEditModal(false);
      setSelectedPlaylist(null);
      setSelectedMediaIds([]);
      
    } catch (error) {
      console.error('Error updating playlist:', error);
      alert(t('errors.playlists.updateError') + ': ' + error.message);
    }
  };

  const deletePlaylist = async (id) => {
    if (!window.confirm(t('playlists.deleteConfirm'))) return;

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/playlists/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await loadData();
      alert(t('success.deleted', { name: t('playlists.title') }));
    } catch (error) {
      alert(t('errors.playlists.deleteError'));
    }
  };

  // ✅ FIXED: openEditModal mit korrekter Media-ID Extraktion
  const openEditModal = async (playlist) => {
    try {
      // Fetch fresh playlist data from backend
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/playlists`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const freshPlaylists = await response.json();
      const freshPlaylist = freshPlaylists.find(p => p.id === playlist.id);
      
      if (freshPlaylist) {
        console.log('📋 Opening playlist with fresh data:', freshPlaylist);
        console.log('📋 Media items in playlist:', freshPlaylist.media_items);
        setSelectedPlaylist(freshPlaylist);
        
        // ✅ FIXED: Sammle MEDIA-IDs (nicht Playlist Item IDs!)
        const existingMediaIds = freshPlaylist.media_items
          ?.filter(item => item.item_type === 'media')
          .map(m => m.media_id || m.id) || []; // Backend sendet media_id
        
        console.log('🎯 Existing media IDs in playlist:', existingMediaIds);
        setSelectedMediaIds(existingMediaIds);
      } else {
        setSelectedPlaylist(playlist);
        const existingMediaIds = playlist.media_items
          ?.filter(item => item.item_type === 'media')
          .map(m => m.media_id || m.id) || [];
        setSelectedMediaIds(existingMediaIds);
      }
      
      setShowEditModal(true);
    } catch (error) {
      console.error('Error loading fresh playlist:', error);
      // Fallback to cached data
      setSelectedPlaylist(playlist);
      const existingMediaIds = playlist.media_items
        ?.filter(item => item.item_type === 'media')
        .map(m => m.media_id || m.id) || [];
      setSelectedMediaIds(existingMediaIds);
      setShowEditModal(true);
    }
  };

  const openPreview = (playlist) => {
    setPreviewPlaylist(playlist);
    setShowPreview(true);
  };

  const toggleMediaSelection = (mediaId) => {
    if (selectedMediaIds.includes(mediaId)) {
      setSelectedMediaIds(selectedMediaIds.filter(id => id !== mediaId));
    } else {
      setSelectedMediaIds([...selectedMediaIds, mediaId]);
    }
  };

  // ✅ FIXED: handleRemoveItem mit korrektem State-Update
  const handleRemoveItem = (item, index) => {
    if (!selectedPlaylist || !selectedPlaylist.media_items) return;
    
    console.log('🗑️ Removing item:', item, 'at index:', index);
    
    const updatedItems = selectedPlaylist.media_items.filter((_, i) => i !== index);
    
    setSelectedPlaylist({
      ...selectedPlaylist,
      media_items: updatedItems
    });
    
    // ✅ FIXED: Update selectedMediaIds wenn es ein Media-Item ist (verwende media_id!)
    if (item.item_type === 'media') {
      const mediaIdToRemove = item.media_id || item.id;
      const updatedMediaIds = selectedMediaIds.filter(id => id !== mediaIdToRemove);
      setSelectedMediaIds(updatedMediaIds);
      console.log('✅ Updated selectedMediaIds:', updatedMediaIds);
    }
    
    console.log('✅ Remaining items:', updatedItems);
  };

  const handleAddWidget = async (widgetData) => {
    try {
      console.log('➕ Adding widget to playlist:', widgetData);
      
      const response = await fetch(
        `${API_URL}/playlists/${selectedPlaylist.id}/widgets`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            feature_id: widgetData.feature_id,
            feature_instance_id: widgetData.feature_instance_id,
            duration: widgetData.duration || 30,
            widget_config: widgetData.config || {}
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || t('errors.widgets.addFailed'));
      }

      const result = await response.json();
      console.log('✅ Widget added successfully:', result);

      await loadData();
      
      const updatedPlaylists = await fetch(`${API_URL}/playlists`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      }).then(res => res.json());
      
      const updatedPlaylist = updatedPlaylists.find(p => p.id === selectedPlaylist.id);
      if (updatedPlaylist) {
        setSelectedPlaylist(updatedPlaylist);
        // ✅ FIXED: Verwende media_id
        const existingMediaIds = updatedPlaylist.media_items
          ?.filter(item => item.item_type === 'media')
          .map(m => m.media_id || m.id) || [];
        setSelectedMediaIds(existingMediaIds);
      }
      
      setShowWidgetPicker(false);
      alert(t('success.widgetAdded'));
      
    } catch (error) {
      console.error('❌ Error adding widget:', error);
      alert(t('errors.widgets.addError') + ': ' + error.message);
    }
  };

  const getMediaIcon = (type) => {
    switch(type) {
      case 'widget':
        return <Zap size={16} className="text-purple-500 flex-shrink-0" />;
      case 'image': 
        return <Image size={16} className="text-blue-600 flex-shrink-0" />;
      case 'video': 
        return <Video size={16} className="text-red-500 flex-shrink-0" />;
      case 'webpage': 
        return <Globe size={16} className="text-green-500 flex-shrink-0" />;
      case 'pdf':
        return <FileText size={16} className="text-orange-500 flex-shrink-0" />;
      default: 
        return <Image size={16} className="text-gray-400 flex-shrink-0" />;
    }
  };

  const getTotalDuration = (playlist) => {
    const totalSeconds = playlist.media_items?.reduce((sum, item) => sum + (item.duration || 0), 0) || 0;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  };

  // ✅ NEW: Get media thumbnail URL
  const getMediaThumbnail = (item) => {
  if (item.item_type === 'widget' || item.type === 'widget') {
    return null;
  }
  
  if (item.type === 'image') {
    // Priority: thumbnail_url > url
    if (item.thumbnail_url) {
      return item.thumbnail_url.startsWith('http')
        ? item.thumbnail_url
        : `${API_BASE_URL}${item.thumbnail_url}`;
    }
    
    // Fallback auf Original URL
    return item.url?.startsWith('http') 
      ? item.url 
      : `${API_BASE_URL}${item.url}`;
  }
  
  return null;
};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">{t('playlists.title')}</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('playlists.playlistCount', { count: playlists.length })}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-primary-600 dark:bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors shadow-md"
        >
          <Plus size={20} /> {t('playlists.create')}
        </button>
      </div>

      {/* Empty State */}
      {playlists.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center border border-gray-200 dark:border-gray-700">
          <Layers className="mx-auto text-gray-400 dark:text-gray-600 mb-4" size={64} />
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
            {t('playlists.noPlaylists')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('playlists.createFirstPlaylist')}
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 bg-primary-600 dark:bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
          >
            <Plus size={20} /> {t('playlists.create')}
          </button>
        </div>
      ) : (
        /* ✅ IMPROVED: Playlists Grid with better visual design */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {playlists.map(playlist => {
            const itemCount = playlist.media_items?.length || 0;
            const totalDuration = getTotalDuration(playlist);
            const mediaCount = playlist.media_items?.filter(i => i.item_type === 'media').length || 0;
            const widgetCount = playlist.media_items?.filter(i => i.item_type === 'widget').length || 0;

            return (
              <div
                key={playlist.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700 overflow-hidden group"
              >
                {/* ✅ NEW: Thumbnail Preview Section */}
                <div className="relative h-40 bg-gradient-to-br from-blue-500 to-purple-600 overflow-hidden">
                  {itemCount > 0 && playlist.media_items.slice(0, 4).some(item => getMediaThumbnail(item)) ? (
                    <div className="grid grid-cols-2 gap-1 h-full p-2">
                      {playlist.media_items.slice(0, 4).map((item, idx) => {
                        const thumbnail = getMediaThumbnail(item);
                        return (
                          <div key={idx} className="relative bg-gray-800 rounded overflow-hidden">
                            {thumbnail ? (
                              <img 
                                src={thumbnail} 
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                {getMediaIcon(item.type)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Layers className="text-white/30" size={64} />
                    </div>
                  )}
                  
                  {/* Item Count Badge */}
                  <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium">
                    {itemCount} {itemCount === 1 ? 'item' : 'items'}
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  {/* Header */}
                  <div className="mb-4">
                    <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-1 truncate">
                      {playlist.name}
                    </h3>
                    {playlist.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                        {playlist.description}
                      </p>
                    )}
                  </div>

                  {/* ✅ IMPROVED: Stats Grid */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
                      <Image size={16} className="mx-auto text-blue-600 dark:text-blue-400 mb-1" />
                      <div className="text-xs text-gray-600 dark:text-gray-400">Media</div>
                      <div className="text-lg font-bold text-gray-900 dark:text-white">{mediaCount}</div>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 text-center">
                      <Zap size={16} className="mx-auto text-purple-600 dark:text-purple-400 mb-1" />
                      <div className="text-xs text-gray-600 dark:text-gray-400">Widgets</div>
                      <div className="text-lg font-bold text-gray-900 dark:text-white">{widgetCount}</div>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                      <Clock size={16} className="mx-auto text-green-600 dark:text-green-400 mb-1" />
                      <div className="text-xs text-gray-600 dark:text-gray-400">Duration</div>
                      <div className="text-sm font-bold text-gray-900 dark:text-white">{totalDuration}</div>
                    </div>
                  </div>

                  {/* ✅ IMPROVED: Item Preview List */}
                  {itemCount > 0 && (
                    <div className="mb-4 space-y-1">
                      {playlist.media_items.slice(0, 3).map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 px-3 py-2 rounded-lg"
                        >
                          {getMediaIcon(item.type)}
                          <span className="flex-1 truncate text-xs">
                            {idx + 1}. {item.name}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                            {item.duration}s
                          </span>
                        </div>
                      ))}
                      {itemCount > 3 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 text-center pt-1">
                          +{itemCount - 3} more
                        </p>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditModal(playlist)}
                      className="flex-1 flex items-center justify-center gap-2 bg-blue-600 dark:bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      <Edit2 size={16} /> {t('common.edit')}
                    </button>
                    <button
                      onClick={() => openPreview(playlist)}
                      className="flex-1 flex items-center justify-center gap-2 bg-green-600 dark:bg-green-600 text-white px-4 py-2.5 rounded-lg hover:bg-green-700 dark:hover:bg-green-700 transition-colors text-sm font-medium"
                    >
                      <Play size={16} /> {t('playlists.preview')}
                    </button>
                    <button
                      onClick={() => deletePlaylist(playlist.id)}
                      className="flex items-center justify-center bg-red-600 dark:bg-red-600 text-white px-4 py-2.5 rounded-lg hover:bg-red-700 dark:hover:bg-red-700 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Playlist Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto transition-colors">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('playlists.createNewPlaylist')}</h3>
              <button 
                onClick={() => setShowCreateModal(false)}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('playlists.name')} *
                </label>
                <input
                  type="text"
                  value={newPlaylist.name}
                  onChange={(e) => setNewPlaylist({ ...newPlaylist, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
                  placeholder={t('playlists.namePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('playlists.description')}
                </label>
                <textarea
                  value={newPlaylist.description}
                  onChange={(e) => setNewPlaylist({ ...newPlaylist, description: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors resize-none"
                  rows="3"
                  placeholder={t('playlists.descriptionPlaceholder')}
                />
              </div>

              <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <input
                  type="checkbox"
                  id="loop"
                  checked={newPlaylist.loop}
                  onChange={(e) => setNewPlaylist({ ...newPlaylist, loop: e.target.checked })}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="loop" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('playlists.loopPlaylist')}
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">
                  {t('playlists.selectMedia')}
                </label>
                {Array.isArray(media) && media.length > 0 ? (
                  <>
                    <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-64 overflow-y-auto bg-white dark:bg-gray-700">
                      {media.map(item => (
                        <div
                          key={item.id}
                          onClick={() => toggleMediaSelection(item.id)}
                          className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 border-b dark:border-gray-600 last:border-b-0 transition-colors ${
                            selectedMediaIds.includes(item.id) ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-l-blue-500' : ''
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={selectedMediaIds.includes(item.id)}
                              onChange={() => {}}
                              className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                            {getMediaIcon(item.type)}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">{item.name}</p>
                              <p className="text-xs text-gray-600 dark:text-gray-400 capitalize">
                                {item.type} • {item.duration}s
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 flex items-center gap-1">
                      <Check size={14} />
                      {t('playlists.itemsSelected', { count: selectedMediaIds.length })}
                    </p>
                  </>
                ) : (
                  <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center bg-gray-50 dark:bg-gray-700">
                    <AlertCircle className="mx-auto text-gray-400 dark:text-gray-500 mb-3" size={48} />
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('playlists.noMediaAvailable')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={createPlaylist}
                disabled={!newPlaylist.name}
                className="flex-1 bg-primary-600 dark:bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 disabled:bg-gray-400 dark:disabled:bg-gray-600 transition-colors font-medium disabled:cursor-not-allowed"
              >
                {t('playlists.createPlaylist')}
              </button>
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-6 py-3 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Playlist Modal */}
      {showEditModal && selectedPlaylist && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto transition-colors">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {t('playlists.editPlaylist')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{selectedPlaylist.name}</p>
              </div>
              <button 
                onClick={() => setShowEditModal(false)}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Playlist Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <List size={16} />
                    {t('playlists.playlistItems')}
                  </label>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Drag to reorder
                  </span>
                </div>
                <div className="space-y-2">
                  {selectedPlaylist.media_items && selectedPlaylist.media_items.length > 0 ? (
                    selectedPlaylist.media_items.map((item, index) => {
                      const isEditing = editingItemId === (item.playlist_item_id || item.id);
                      const isDragging = draggedItem?.index === index;
                      const isDragOver = dragOverIndex === index;
                      
                      return (
                        <div
                          key={index}
                          draggable={!isEditing}
                          onDragStart={(e) => handleDragStart(e, item, index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, index)}
                          onDragEnd={handleDragEnd}
                          className={`flex items-center gap-3 p-4 rounded-lg transition-all ${
                            isDragging 
                              ? 'opacity-50 bg-blue-100 dark:bg-blue-900/30 scale-95' 
                              : isDragOver
                              ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-400 scale-105'
                              : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                          } ${!isEditing ? 'cursor-move' : ''}`}
                        >
                          <GripVertical 
                            size={20} 
                            className={`text-gray-400 ${!isEditing ? 'cursor-move' : 'cursor-not-allowed opacity-50'}`}
                          />
                          
                          {getMediaIcon(item.type)}
                          
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                              {index + 1}. {item.name}
                            </p>
                            <p className="text-xs text-gray-600 dark:text-gray-400 capitalize">
                              {item.item_type === 'widget' ? (item.feature_id || 'widget') : item.type}
                            </p>
                          </div>
                          
                          {/* Duration Edit */}
                          <div className="flex items-center gap-2">
                            {isEditing ? (
                              <>
                                <input
                                  type="number"
                                  min="1"
                                  max="3600"
                                  value={editDuration}
                                  onChange={(e) => setEditDuration(e.target.value)}
                                  onKeyPress={(e) => handleDurationKeyPress(e, selectedPlaylist.id, item.playlist_item_id || item.id)}
                                  className="w-20 px-3 py-1.5 text-sm border-2 border-blue-500 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                                  autoFocus
                                />
                                <button
                                  onClick={() => updateItemDuration(selectedPlaylist.id, item.playlist_item_id || item.id, editDuration)}
                                  className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 rounded transition-colors"
                                  title={t('common.save')}
                                >
                                  <Check size={18} />
                                </button>
                                <button
                                  onClick={cancelEditingDuration}
                                  className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                                  title={t('common.cancel')}
                                >
                                  <X size={18} />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => startEditingDuration(item)}
                                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors font-mono"
                                title="Click to edit duration"
                              >
                                {item.duration}s
                              </button>
                            )}
                          </div>
                          
                          <button
                            onClick={() => handleRemoveItem(item, index)}
                            className="p-2 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            title={t('playlists.removeFromPlaylist')}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                      <Layers size={48} className="mx-auto mb-3 opacity-50" />
                      <p>{t('playlists.noItems')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* ✅ FIXED: Add More Media mit korrektem Filter */}
              <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Plus size={16} />
                  {t('playlists.addMoreMedia')}
                </label>
                {(() => {
                  // ✅ FIXED: Dynamischer Filter mit media_id
                  const playlistMediaIds = (selectedPlaylist.media_items || [])
                    .filter(item => item.item_type === 'media')
                    .map(item => item.media_id || item.id);
                  
                  const availableMedia = Array.isArray(media) 
                    ? media.filter(m => !playlistMediaIds.includes(m.id))
                    : [];

                  console.log('🔍 Filter check:', {
                    playlistMediaIds,
                    totalMedia: media.length,
                    availableMedia: availableMedia.length
                  });
                  
                  return availableMedia.length > 0 ? (
                    <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-48 overflow-y-auto bg-white dark:bg-gray-700">
                      {availableMedia.map(item => (
                        <div
                          key={item.id}
                          onClick={() => {
                            console.log('➕ Adding media:', item.name);
                            
                            // ✅ Add to playlist items with correct structure
                            const newItem = {
                              id: item.id,
                              media_id: item.id, // ✅ WICHTIG: Setze media_id explizit!
                              name: item.name,
                              type: item.type,
                              duration: item.duration,
                              url: item.url,
                              file_path: item.file_path,
                              item_type: 'media'
                            };
                            
                            const updatedItems = [...(selectedPlaylist.media_items || []), newItem];
                            
                            setSelectedPlaylist({
                              ...selectedPlaylist,
                              media_items: updatedItems
                            });
                            
                            // ✅ Update selectedMediaIds
                            setSelectedMediaIds([...selectedMediaIds, item.id]);
                            
                            console.log('✅ Item added to playlist');
                          }}
                          className="p-4 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 border-b dark:border-gray-600 last:border-b-0 transition-colors group"
                        >
                          <div className="flex items-center gap-3">
                            {getMediaIcon(item.type)}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400">
                                {item.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                {item.type} • {item.duration}s
                              </p>
                            </div>
                            <Plus size={18} className="text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-300 dark:border-gray-600">
                      <Check size={48} className="mx-auto mb-3 text-green-500" />
                      <p className="font-medium">{t('playlists.allMediaAdded')}</p>
                    </div>
                  );
                })()}
              </div>

              {/* Add Widget Section */}
              <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <Zap size={18} className="text-purple-600 dark:text-purple-400" />
                      {t('playlists.addWidget')}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {t('playlists.addWidgetDescription')}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowWidgetPicker(true)}
                    className="flex items-center gap-2 bg-purple-600 text-white px-5 py-2.5 rounded-lg hover:bg-purple-700 transition-colors shadow-md font-medium"
                  >
                    <Zap size={18} />
                    {t('playlists.addWidget')}
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => updatePlaylistItems(selectedPlaylist.id)}
                className="flex-1 bg-primary-600 dark:bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors font-medium shadow-md"
              >
                {t('common.save')}
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-6 py-3 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewPlaylist && (
        <PlaylistPreview
          playlist={previewPlaylist}
          media={media}
          onClose={() => {
            setShowPreview(false);
            setPreviewPlaylist(null);
          }}
        />
      )}

      {/* Widget Picker Modal */}
      {showWidgetPicker && (
        <WidgetPicker
          onSelect={handleAddWidget}
          onCancel={() => setShowWidgetPicker(false)}
        />
      )}
    </div>
  );
};

export default PlaylistManager;